/// <reference types="@angular/localize" />

import { inject, Service } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { authenticationControllerLogoutV1 } from '@lexplus-authentication-api';
import { API_CONFIGURATION } from '@lexplus-environments';
import {
  ImpersonationStateClear,
  LocalizedRouteService,
  notify,
  ProfileStateClear,
  ProfileStateSetIsLoggedIn,
  TemporarySessionStateClear,
} from '@lexplus-shared-lib';
import { AUTH_ROUTE_IDS, LOCALIZED_AUTH_ROUTES } from '../auth-route-const';
import { LocalDevAutoLoginService } from './local-dev-auto-login.service';

/**
 * Ends the session and shows the login form. The page on show stays untouched until the
 * login route has replaced it: only `isLoggedIn` is reset before navigating, which the
 * guards read, while the profile the app shell renders from is cleared afterwards.
 */
@Service()
export class LogoutService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly localizedRouteService = inject(LocalizedRouteService);
  private readonly localDevAutoLogin = inject(LocalDevAutoLoginService);

  private runningLogout: Promise<void> | null = null;

  /** Joins a logout already under way instead of starting a second one. */
  public logout(): Promise<void> {
    this.runningLogout ??= this.performLogout().finally(() => (this.runningLogout = null));
    return this.runningLogout;
  }

  private async performLogout(): Promise<void> {
    await this.endServerSession();

    this.store.dispatch(new ProfileStateSetIsLoggedIn(false));
    // Leaves the login form to the user on a localhost dev server; no-op everywhere else.
    this.localDevAutoLogin.suppress();

    try {
      await this.router.navigateByUrl(
        `/${this.localizedRouteService.getLocalizedRoute(LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.ROOT], LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.LOGIN])}`,
      );
    } finally {
      // ProfileStateClear keeps the appDeviceId, the device trust behind the 2FA skip.
      this.store.dispatch(new ProfileStateClear());
      this.store.dispatch(new TemporarySessionStateClear());
      this.store.dispatch(new ImpersonationStateClear());
    }
  }

  /** A failed call still ends the local session; a 401 means the server session had already expired. */
  private async endServerSession(): Promise<void> {
    try {
      await lastValueFrom(authenticationControllerLogoutV1(this.httpClient, this.apiConfiguration.baseUrlAuth, { body: {} }));
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return;
      }
      notify.error($localize`:@@auth.logout.failed:Abmeldung fehlgeschlagen`, {
        description: $localize`:@@auth.logout.failedDescription:Bei der Abmeldung ist ein Fehler aufgetreten.`,
      });
    }
  }
}
